import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '@festpix/services';
import { getUserWallet, createWalletForUser, getChildWallets, Wallet } from '@festpix/services';
import { colors, spacing, borderRadius, typography, shadows } from '@festpix/ui';
import AttendeeLayout from '../../components/AttendeeLayout';

export default function HomeScreen() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [childWallets, setChildWallets] = useState<Wallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingWallet, setCreatingWallet] = useState(false);
  const user = auth.currentUser;

  const loadWallet = useCallback(async () => {
    const maxRetries = 3;
    const retryDelay = 1000;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const userWallet = await getUserWallet(currentUser.uid);
          if (userWallet) {
            setWallet(userWallet);
            const children = await getChildWallets(userWallet.id, currentUser.uid);
            setChildWallets(children);
            setLoading(false);
            return;
          }

          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        }
      } catch (error) {
        console.error('Error loading wallet:', error);
        if (attempt === maxRetries) {
          setLoading(false);
        }
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  const handleCreateWallet = async () => {
    setCreatingWallet(true);
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const newWallet = await createWalletForUser(
          currentUser.uid,
          currentUser.email || '',
          currentUser.email?.split('@')[0] || ''
        );
        setWallet(newWallet);
      }
    } catch (error) {
      console.error('Error creating wallet:', error);
    } finally {
      setCreatingWallet(false);
    }
  };

  if (loading) {
    return (
      <AttendeeLayout>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Carregando...</p>
        </div>
      </AttendeeLayout>
    );
  }

  if (!wallet) {
    return (
      <AttendeeLayout>
        <div style={styles.innerContainer}>
          <div style={styles.emptyStateCard}>
            <span style={styles.emptyStateIcon}>💳</span>
            <h2 style={styles.emptyStateTitle}>Crie sua Carteira</h2>
            <p style={styles.emptyStateText}>
              Voce ainda nao possui uma carteira digital. Crie agora para comecar a usar!
            </p>
            <button
              style={styles.primaryButton}
              onClick={handleCreateWallet}
              disabled={creatingWallet}
            >
              {creatingWallet ? 'Criando...' : 'Criar Minha Carteira'}
            </button>
          </div>
        </div>
      </AttendeeLayout>
    );
  }

  return (
    <AttendeeLayout>
      <div style={styles.innerContainer}>
      {/* Welcome Section */}
      <div style={styles.welcomeSection}>
        <h1 style={styles.greeting}>
          Olá, {user?.displayName || user?.email?.split('@')[0] || 'Usuário'}!
        </h1>
        <p style={styles.subGreeting}>Sua carteira digital</p>
      </div>

      {/* Balance Card */}
      <div style={styles.balanceCard}>
        <div style={styles.balanceHeader}>
          <span style={styles.balanceLabel}>Saldo Disponível</span>
          <span style={styles.balanceBadge}>Ativo</span>
        </div>
        <p style={styles.balanceAmount}>
          R$ {wallet.balance.toFixed(2).replace('.', ',')}
        </p>
        <button
          style={styles.rechargeCardButton}
          onClick={() => navigate('/recharge')}
        >
          + Adicionar Saldo
        </button>
      </div>

      {/* Child Wallets Section */}
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>Carteiras dos Filhos</h2>
        <button
          style={styles.addChildButton}
          onClick={() => navigate('/create-child-wallet', { state: { parentWallet: wallet } })}
        >
          + Adicionar
        </button>
      </div>

      {childWallets.length > 0 ? (
        <div style={styles.childWalletsContainer}>
          {childWallets.map((child) => (
            <div key={child.id} style={styles.childWalletCard}>
              <div style={styles.childWalletInfo}>
                <div style={styles.childAvatarContainer}>
                  <span style={styles.childAvatar}>👦</span>
                </div>
                <div style={styles.childDetails}>
                  <p style={styles.childName}>{child.childName || 'Filho'}</p>
                  <p style={styles.childBalance}>
                    R$ {child.balance.toFixed(2).replace('.', ',')}
                  </p>
                </div>
              </div>
              <button
                style={styles.transferButton}
                onClick={() => navigate('/transfer', { state: { childWallet: child, parentWallet: wallet } })}
              >
                Transferir
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.emptyChildWallets}>
          <p style={styles.emptyChildWalletsText}>
            Nenhuma carteira filho criada. Toque em "+ Adicionar" para criar.
          </p>
        </div>
      )}

      {/* Quick Actions */}
      <h2 style={styles.sectionTitle}>Ações Rápidas</h2>
      <div style={styles.actionsGrid}>
        <button style={styles.actionCard} onClick={() => navigate('/qr')}>
          <div style={{ ...styles.actionIcon, backgroundColor: colors.primary50 }}>
            <span style={styles.actionEmoji}>📱</span>
          </div>
          <p style={styles.actionTitle}>QR Code</p>
          <p style={styles.actionSubtitle}>Mostrar para pagar</p>
        </button>

        <button style={styles.actionCard} onClick={() => navigate('/transactions')}>
          <div style={{ ...styles.actionIcon, backgroundColor: colors.infoBg }}>
            <span style={styles.actionEmoji}>📋</span>
          </div>
          <p style={styles.actionTitle}>Histórico</p>
          <p style={styles.actionSubtitle}>Ver transações</p>
        </button>
      </div>

      </div>
    </AttendeeLayout>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  innerContainer: {
    paddingBottom: spacing.xl,
  },
  container: {
    minHeight: '100vh',
    backgroundColor: colors.background,
    padding: spacing.lg,
    paddingBottom: spacing.xl,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: colors.background,
  },
  spinner: {
    width: 40,
    height: 40,
    border: `3px solid ${colors.border}`,
    borderTop: `3px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: spacing.md,
    color: colors.textSecondary,
  },

  // Welcome Section
  welcomeSection: {
    marginBottom: spacing.lg,
  },
  greeting: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  subGreeting: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },

  // Balance Card
  balanceCard: {
    backgroundColor: colors.primaryDark,
    padding: spacing.lg,
    borderRadius: borderRadius.xl,
    marginBottom: spacing.lg,
    boxShadow: shadows.lg,
  },
  balanceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  balanceLabel: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: typography.fontSize.sm,
  },
  balanceBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: `${spacing.xs}px ${spacing.sm}px`,
    borderRadius: borderRadius.full,
    color: colors.textLight,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
  },
  balanceAmount: {
    color: colors.textLight,
    fontSize: typography.fontSize['4xl'],
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.md,
  },
  rechargeCardButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: `${spacing.sm}px ${spacing.md}px`,
    borderRadius: borderRadius.md,
    color: colors.textLight,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    border: 'none',
    cursor: 'pointer',
    width: '100%',
  },

  // Section Header
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    margin: 0,
    marginBottom: spacing.md,
  },
  addChildButton: {
    backgroundColor: colors.primary50,
    padding: `${spacing.xs}px ${spacing.sm}px`,
    borderRadius: borderRadius.md,
    color: colors.primary,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    border: 'none',
    cursor: 'pointer',
  },

  // Child Wallets
  childWalletsContainer: {
    marginBottom: spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  childWalletCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: shadows.sm,
  },
  childWalletInfo: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  childAvatarContainer: {
    width: 44,
    height: 44,
    borderRadius: borderRadius.full,
    backgroundColor: colors.infoBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  childAvatar: {
    fontSize: 22,
  },
  childDetails: {
    flex: 1,
  },
  childName: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    margin: 0,
    marginBottom: 2,
  },
  childBalance: {
    fontSize: typography.fontSize.sm,
    color: colors.success,
    fontWeight: typography.fontWeight.medium,
    margin: 0,
  },
  transferButton: {
    backgroundColor: colors.primary,
    padding: `${spacing.sm}px ${spacing.md}px`,
    borderRadius: borderRadius.md,
    color: colors.textLight,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    border: 'none',
    cursor: 'pointer',
  },
  emptyChildWallets: {
    backgroundColor: colors.neutral100,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    marginBottom: spacing.lg,
  },
  emptyChildWalletsText: {
    color: colors.textSecondary,
    fontSize: typography.fontSize.sm,
    textAlign: 'center',
    margin: 0,
  },

  // Actions Grid
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  actionCard: {
    backgroundColor: colors.cardBackground,
    padding: spacing.lg,
    borderRadius: borderRadius.lg,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxShadow: shadows.sm,
    border: 'none',
    cursor: 'pointer',
  },
  actionIcon: {
    width: 56,
    height: 56,
    borderRadius: borderRadius.lg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  actionEmoji: {
    fontSize: 24,
  },
  actionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    margin: 0,
    marginBottom: spacing.xs,
  },
  actionSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    margin: 0,
  },

  // Buttons
  primaryButton: {
    backgroundColor: colors.primary,
    padding: `${spacing.md}px ${spacing.lg}px`,
    borderRadius: borderRadius.md,
    color: colors.textLight,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    width: '100%',
    boxShadow: shadows.colored,
    border: 'none',
    cursor: 'pointer',
  },
  logoutButton: {
    backgroundColor: 'transparent',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    color: colors.textSecondary,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    width: '100%',
    border: `1px solid ${colors.border}`,
    cursor: 'pointer',
  },

  // Empty State
  emptyStateCard: {
    backgroundColor: colors.cardBackground,
    padding: spacing.xl,
    borderRadius: borderRadius.xl,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: spacing.lg,
    boxShadow: shadows.md,
  },
  emptyStateIcon: {
    fontSize: 48,
    marginBottom: spacing.md,
  },
  emptyStateTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  emptyStateText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.lg,
    lineHeight: 1.5,
  },
};
